import { useState, useEffect, useCallback } from 'react';
import { Layers, Users, Activity, Monitor, Globe, Store, Database } from 'lucide-react';
import { Progress } from '../../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

function DistCard({ title, icon: Icon, data, emptyMsg, color }) {
  const entries = data ? Object.entries(data).sort((a, b) => b[1] - a[1]) : [];
  const maxVal = entries.length > 0 ? entries[0][1] : 1;
  const barColor = { violet: 'bg-violet-500', cyan: 'bg-cyan-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500', pink: 'bg-pink-500', blue: 'bg-blue-500' }[color] || 'bg-white/20';
  return (
    <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-5 space-y-3" data-testid={`dist-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h3 className="text-white font-bold text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-white/50" />{title}</h3>
      {entries.length === 0 ? (<p className="text-white/20 text-xs">{emptyMsg}</p>) : (
        <div className="space-y-2">
          {entries.slice(0, 8).map(([key, val]) => (
            <div key={key}>
              <div className="flex justify-between text-xs mb-1"><span className="text-white/50 truncate capitalize">{key}</span><span className="text-white/70 font-bold">{val}</span></div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className={`h-full ${barColor} rounded-full`} style={{ width: `${(val / maxVal) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminFeatures() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFeatures = useCallback(async () => {
    try { const res = await fetch(`${API}/api/admin/features`, { credentials: 'include' }); if (res.ok) setData(await res.json()); } catch (e) { console.error("Features fetch error:", e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-white/30 text-center py-16">Failed to load feature data</div>;

  const lc = data.layer_counts || {};
  const lu = data.layer_users || {};
  const meta = data.layers_meta || {};
  const store = data.store || {};

  return (
    <div className="space-y-6" data-testid="admin-features">
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Feature Usage</h1>
        <p className="text-white/40 text-sm mt-1">All 6 PI layers, platforms, sources, store analytics</p>
      </div>

      {/* Layer usage grid */}
      <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-5">
        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Layers className="w-4 h-4 text-violet-400" /> PI Layer Usage</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(meta).map(([key, m]) => {
            const count = lc[key] || 0;
            const users = lu[key] || 0;
            const total = lc.total || 1;
            const pct = Math.round((count / total) * 100);
            return (
              <div key={key} className="border border-white/[0.06] rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }} />
                  <span className="text-white font-bold text-sm">{m.name}</span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-white text-2xl font-bold">{count.toLocaleString()}</span>
                  <span className="text-white/30 text-xs">events</span>
                  <span className="text-white/20 text-xs">·</span>
                  <span className="text-white/50 text-xs">{users} users</span>
                </div>
                <Progress value={pct} className="h-1.5 bg-white/[0.06]" />
                <span className="text-white/30 text-[10px]">{pct}% of total</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribution cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <DistCard title="Source Distribution" icon={Globe} data={data.source_counts} emptyMsg="No source data" color="cyan" />
        <DistCard title="AI Platform Usage" icon={Monitor} data={data.platform_counts} emptyMsg="No platform data yet" color="amber" />
        <DistCard title="Receiver Outcomes" icon={Users} data={data.receiver_outcome_distribution} emptyMsg="No outcome data" color="violet" />
        <DistCard title="Relationship Contexts" icon={Activity} data={data.relationship_context_distribution} emptyMsg="No context data" color="emerald" />
        <DistCard title="Tone Distribution" icon={Activity} data={data.tone_distribution} emptyMsg="No tone data" color="pink" />
        <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-5 space-y-3">
          <h3 className="text-white font-bold text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-amber-400" /> Two-Stage Analysis</h3>
          <div className="grid grid-cols-3 gap-3">
            <div><p className="text-white font-bold text-lg">{data.two_stage_analysis?.total_analyses || 0}</p><p className="text-white/30 text-xs">Total</p></div>
            <div><p className="text-white font-bold text-lg">{data.two_stage_analysis?.stage2_completions || 0}</p><p className="text-white/30 text-xs">Stage 2</p></div>
            <div><p className="text-emerald-400 font-bold text-lg">{data.two_stage_analysis?.completion_rate || 0}%</p><p className="text-white/30 text-xs">Rate</p></div>
          </div>
        </div>
      </div>

      {/* Store Analytics */}
      <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-5">
        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Store className="w-4 h-4 text-blue-400" /> PI Store Analytics</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{store.total_packages || 0}</p><p className="text-white/30 text-xs">Packages</p></div>
          <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{(store.total_casts_seeded || 0).toLocaleString()}</p><p className="text-white/30 text-xs">Seeded Casts</p></div>
          <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{store.user_casts_created || 0}</p><p className="text-white/30 text-xs">User Casts</p></div>
          <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{store.package_access_granted || 0}</p><p className="text-white/30 text-xs">Access Granted</p></div>
        </div>
        {store.top_packages?.length > 0 && (
          <div>
            <h3 className="text-white/50 text-xs font-bold mb-2">Top Packages by Users</h3>
            <div className="space-y-1">
              {store.top_packages.map(p => (
                <div key={p.package_id} className="flex items-center justify-between bg-white/[0.02] rounded px-3 py-1.5">
                  <span className="text-white/60 text-xs truncate">{p.name}</span>
                  <span className="text-white/40 text-xs">{p.users} users</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {store.categories?.length > 0 && (
          <div className="mt-3">
            <h3 className="text-white/50 text-xs font-bold mb-2">Package Categories</h3>
            <div className="flex flex-wrap gap-2">
              {store.categories.map(c => (
                <span key={c.name} className="px-2.5 py-1 text-[10px] font-medium rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">{c.name} ({c.count})</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Top domains */}
      {data.top_domains?.length > 0 && (
        <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-5">
          <h2 className="text-white font-bold text-sm mb-3 flex items-center gap-2"><Database className="w-4 h-4 text-emerald-400" /> Top Domains</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {data.top_domains.map(d => (
              <div key={d.domain} className="flex justify-between items-center bg-white/[0.03] rounded-lg px-3 py-2">
                <span className="text-white/50 text-xs truncate capitalize">{d.domain}</span>
                <span className="text-white/70 text-xs font-bold ml-2">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
